import { useEffect, useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { isSmartFolder } from './isSmartFolder.ts';
import { useSmartFolderSync } from './useSmartFolderSync.ts';
import type { useSmartFolderAncestorSyncQuery } from './__generated__/useSmartFolderAncestorSyncQuery.graphql.ts';

const SmartFolderAncestorsQuery = graphql`
	query useSmartFolderAncestorSyncQuery($contentId: ID!) {
		ptpage(id: $contentId, status: [CURRENT, DRAFT]) {
			id
			type
			subType
			nearestAncestors {
				nodes {
					id
					type
					subType
				}
			}
		}
	}
`;

/**
 * For ANY piece of Confluence content (page, blog post, folder, etc.), look
 * up its ancestor chain and — if the highest ancestor is a smart (MANAGED)
 * folder — trigger a sync of that root smart folder.
 *
 * This is what powers "open a deep link to a page inside a smart folder and
 * the entire managed tree gets refreshed". The folder view itself uses
 * `useSmartFolderSync` directly; this hook covers all other content types.
 *
 * Safe to call when `contentId` is empty/falsy — in that case the query is
 * skipped and no sync is triggered.
 */
export const useSmartFolderAncestorSync = (
	contentId: string | null | undefined,
): { isInSmartFolderTree: boolean } => {
	const { data } = useQueryInterop<
		useSmartFolderAncestorSyncQuery['response'],
		useSmartFolderAncestorSyncQuery['variables']
	>(SmartFolderAncestorsQuery, {
		variables: { contentId: contentId ?? '' },
		skip: !contentId,
		// cache-first avoids unnecessary network requests — the page tree query
		// typically already loaded the ptpage data we need.
		fetchPolicy: 'cache-first',
	});

	const { triggerSync } = useSmartFolderSync();
	const lastTriggeredSyncKeyRef = useRef<string | null>(null);

	const syncTargetId = useMemo<string | null>(() => {
		if (!contentId) {
			return null;
		}
		const ptpage = data?.ptpage;
		if (!ptpage || ptpage.id !== contentId) {
			return null;
		}

		// If the current content IS itself a smart folder, FolderOverview
		// will handle the sync; this hook only covers content nested inside
		// a smart folder.
		if (isSmartFolder(ptpage)) {
			return null;
		}

		const ancestors = ptpage?.nearestAncestors?.nodes;
		if (!ancestors || ancestors.length === 0) {
			return null;
		}

		// Find the highest (root) MANAGED ancestor, if any.
		// `nearestAncestors` is ordered nearest-first, so iterate in reverse
		// to find the topmost smart folder.
		for (let i = ancestors.length - 1; i >= 0; i--) {
			const ancestor = ancestors[i];
			if (ancestor && isSmartFolder(ancestor)) {
				return ancestor.id;
			}
		}

		return null;
	}, [contentId, data?.ptpage]);

	// syncTargetId is non-null iff a smart folder ancestor exists;
	// also handle the case where the content itself IS a smart folder.
	const isInSmartFolderTree = useMemo(
		() => (data?.ptpage ? isSmartFolder(data.ptpage) : false) || syncTargetId !== null,
		[data?.ptpage, syncTargetId],
	);

	// Include `contentId` so that navigating away from and back to a page
	// nested inside the same smart folder re-triggers sync even though
	// `syncTargetId` resolves to the same root folder ID both times.
	useEffect(() => {
		if (!syncTargetId) {
			lastTriggeredSyncKeyRef.current = null;
			return;
		}

		const syncKey = `${contentId}:${syncTargetId}`;
		if (lastTriggeredSyncKeyRef.current === syncKey) {
			return;
		}

		lastTriggeredSyncKeyRef.current = syncKey;
		triggerSync(syncTargetId);
	}, [contentId, syncTargetId, triggerSync]);

	return { isInSmartFolderTree };
};
